// Record a meeting, 6.3 (Record.dc.html, Kareem: "I like the new record a meeting"): the step header
// (1 Record › 2 Process › 3 Review), the Process list driven by where the minutes job has got to (Recording.stage), and
// the Review step on the page: the draft minutes (summary, decisions, the actions and meetings to set up, each can be
// left out), where they will be filed (Change picks another meeting), who is speaking (only when the voice service
// separated voices), a transcript excerpt, then Send to Requests for approval, or Discard. The card is made when the
// minutes are written and waits in Requests; nothing is filed until a person approves it there.
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeetingRow, ProposedAction, Recording, RecordingStage } from '@dpw/shared';
import { api } from '../api';
import { useMe, useTeammates } from '../lib/queries';
import { useCardChange } from '../lib/decide';
import { ErrorNote } from '../ui/bits';
import { DrawnCheck, Skel } from '../ui/motion';
import { gbDate } from '../lib/format';
import { clock, NameSpeaker, speakerColor, speakerView } from './parts';

export type RecStep = 1 | 2 | 3;

/** 1 Record › 2 Process › 3 Review: the current step in ink, the ones behind it ticked. */
export function StepHead({ step }: { step: RecStep }) {
  const names = ['Record', 'Process', 'Review'];
  return (
    <ol className="rsteps" aria-label="Steps">
      {names.map((n, i) => (
        <li key={n} className={i + 1 === step ? 'on' : i + 1 < step ? 'done' : ''} aria-current={i + 1 === step ? 'step' : undefined}>
          <span className="n">{i + 1 < step ? '✓' : i + 1}</span>{n}
        </li>
      ))}
    </ol>
  );
}

const STAGES: RecordingStage[] = ['transcribing', 'drafting', 'matching'];

/**
 * Process: Transcribing, Drafting minutes, decisions and actions, Matching to tracks and owners. Where it is comes
 * from the server (the parts left, then the finish job's stage); sending the recording counts as transcribing.
 */
export function ProcessList({ rec, sending }: { rec: Recording | null; sending: boolean }) {
  const at = sending || !rec ? 0 : rec.status === 'ready' ? 3 : STAGES.indexOf(rec.stage ?? 'transcribing');
  const voices = new Set((rec?.lines ?? []).map(l => l.who)).size;
  const rows = [
    at > 0 ? `Transcribed${voices ? `, ${voices} ${voices === 1 ? 'speaker' : 'speakers'} detected` : ''}` : 'Transcribing',
    'Drafting minutes, decisions and actions',
    'Matching to tracks and owners',
  ];
  return (
    <div className="steps" role="list" aria-label="Writing the minutes">
      {rows.map((s, i) => (
        <div key={i} role="listitem" className={`step${i < at ? ' done' : i === at ? ' on' : ''}`} aria-current={i === at ? 'step' : undefined}>
          <span className="s">{i < at ? '✓' : ''}</span>{s}
        </div>
      ))}
    </div>
  );
}

/** A recording's card part: an action or a meeting to set up, with what the server keeps for it. */
interface Extra { id: string; label: string; include: boolean; kind: string; action?: { title: string; ownerIds?: string[]; dueDate?: string | null; speaker?: string } }
const extrasOf = (a: ProposedAction | undefined): Extra[] => {
  const x = (a?.origin as { extras?: unknown } | undefined)?.extras;
  return Array.isArray(x) ? (x as Extra[]).filter(e => e && typeof e.id === 'string') : [];
};

/** Share of talk time per voice: each line lasts until the next one starts (at most 45 seconds). */
export function talkShare(lines: Recording['lines']): Map<string, number> {
  const secs = new Map<string, number>();
  lines.forEach((l, i) => {
    const next = lines[i + 1];
    const d = Math.max(1, Math.min(45, next ? next.at - l.at : l.t.split(/\s+/).length * 0.4));
    secs.set(l.who, (secs.get(l.who) ?? 0) + d);
  });
  const total = [...secs.values()].reduce((a, b) => a + b, 0) || 1;
  return new Map([...secs].map(([k, v]) => [k, Math.round((v / total) * 100)]));
}

/** "1 h 24 min" past an hour, else "12:05". */
const length = (s: number) => (s >= 3600 ? `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min` : clock(s));
const dueLabel = (d: string) => gbDate(new Date(`${d}T12:00:00Z`), { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).replace(',', '');

/** Step 3: the draft minutes on the page, to check, then send to Requests for approval (or discard). */
export function ReviewStep({ rec, onAnother, onDiscarded }: { rec: Recording; onAnother: () => void; onDiscarded: () => void }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const m = rec.minutes!;
  const card = useQuery({ queryKey: ['action', rec.actionId], queryFn: () => api.actions.get(rec.actionId!), enabled: !!rec.actionId });
  const a = card.data;
  const me = useMe().data;
  const people = [...(me ? [{ id: me.id, name: me.name }] : []), ...(useTeammates().data ?? [])];
  const extras = extrasOf(a);
  const put = (x: ProposedAction) => qc.setQueryData(['action', x.id], x);
  const tick = useCardChange((x: { id: string; include: boolean }) => api.actions.extra(a!.id, x.id, x.include).then(r => { put(r); return r; }));
  const discard = useMutation({
    mutationFn: () => api.actions.ignore(a!.id),
    onSuccess: r => { put(r); for (const k of ['actions', 'summary', 'notifications', 'meetings']) void qc.invalidateQueries({ queryKey: [k] }); onDiscarded(); },
  });
  const waiting = a?.status === 'waiting';
  /** Who will own it: a voice's name (confirmed, or the guess); a voice nobody named goes to whoever approves. */
  const ownerOf = (x: Extra['action']) => {
    if (!x) return null;
    if (x.speaker) { const v = speakerView(x.speaker, rec.speakers); return v.state === 'unknown' ? null : v.name; }
    const names = (x.ownerIds ?? []).map(id => people.find(p => p.id === id)?.name).filter(Boolean);
    return names.length ? names.join(', ') : null;
  };
  const actions = extras.filter(e => e.kind === 'action');
  const setups = extras.filter(e => e.kind === 'setup');
  return (
    <div className="rvw">
      <section className="panel rvmain" aria-label="Draft minutes">
        <div className="rowc btwc" style={{ alignItems: 'flex-start', gap: 12 }}>
          <div className="col" style={{ gap: 6 }}>
            <span className="lbl upd" style={{ alignSelf: 'flex-start' }}>Draft minutes by the assistant</span>
            <h2 className="rvt">{rec.title}</h2>
            <span className="xs mut">{gbDate(new Date(rec.startedAt), { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Riyadh' }).replace(',', '')} · {length(rec.durationSecs ?? 0)} recorded · {rec.marks.length} marked {rec.marks.length === 1 ? 'moment' : 'moments'}</span>
          </div>
          <span className="sub2" role="status">Minutes are ready for your review</span>
        </div>
        <div className="rvg2">
          <div className="rvsec"><span className="k">Summary</span><p className="sm">{m.summary}</p></div>
          <div className="rvsec"><span className="k">Decisions</span>
            {m.decisions.length ? <ul className="rvul">{m.decisions.map(d => <li key={d} className="sm">{d}</li>)}</ul> : <p className="sm mut">No decisions were heard.</p>}
          </div>
        </div>
        <div className="rvsec">
          <span className="k">Action items · {actions.filter(x => x.include).length} of {actions.length}</span>
          {!a && card.isLoading && <Skel h={44} r={10} />}
          {a && !actions.length && <p className="sm mut">No actions were heard.</p>}
          <div className="xtras" role="group" aria-label="Action items">
            {actions.map(x => {
              const who = ownerOf(x.action);
              return (
                <div className="lrow xtra" key={x.id} data-extra={x.id}>
                  <button type="button" className={`chk${x.include ? ' on' : ''}`} role="checkbox" aria-checked={x.include} aria-label={x.action?.title ?? x.label}
                    disabled={tick.isPending || !waiting} onClick={() => tick.mutate({ id: x.id, include: !x.include })}>{x.include && <DrawnCheck />}</button>
                  <div className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
                    <span className={`sm${x.include ? '' : ' left'}`}>{x.action?.title ?? x.label}</span>
                    <span className="xs mut">{who ?? 'Whoever approves'} · {x.action?.dueDate ? `due ${dueLabel(x.action.dueDate)}` : 'no due date'}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {setups.length > 0 && <>
            <span className="k" style={{ marginTop: 10 }}>Meetings to set up</span>
            <div className="xtras" role="group" aria-label="Meetings to set up">
              {setups.map(x => (
                <div className="lrow xtra" key={x.id}>
                  <button type="button" className={`chk${x.include ? ' on' : ''}`} role="checkbox" aria-checked={x.include} aria-label={x.label}
                    disabled={tick.isPending || !waiting} onClick={() => tick.mutate({ id: x.id, include: !x.include })}>{x.include && <DrawnCheck />}</button>
                  <span className={`sm${x.include ? '' : ' left'}`} style={{ flex: 1 }}>{x.label}</span>
                </div>
              ))}
            </div>
          </>}
          {tick.error ? <ErrorNote error={tick.error} /> : null}
        </div>
        {a && !waiting && <div className="xs mut">This card was already {a.status === 'ignored' ? 'discarded' : a.status}. <Link className="ulink" to={`/requests?id=${a.id}`}>Open it in Requests</Link></div>}
        <div className="rowc" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="bt pri" disabled={!a || !waiting} onClick={() => nav(`/requests?id=${a!.id}`)}>Send to Requests for approval</button>
          <button type="button" className="bt" disabled={!a || !waiting || discard.isPending} onClick={() => discard.mutate()}>Discard</button>
          <span className="sp" />
          <button type="button" className="bt sm" onClick={onAnother}>Record another</button>
        </div>
        {discard.error ? <ErrorNote error={discard.error} /> : null}
        <div className="xs mut">Nothing is filed until it is approved in Requests. Discard keeps the recording and transcript with the meeting.</div>
      </section>
      <aside className="col rvside" style={{ gap: 12 }}>
        <FileTo a={a} canChange={waiting} onChanged={put} />
        <WhoIsSpeaking rec={rec} />
        <Excerpt rec={rec} />
      </aside>
    </div>
  );
}

/** Will file to: Space › Track and the meeting; Change files them on another meeting or workshop (saved on the card). */
function FileTo({ a, canChange, onChanged }: { a: ProposedAction | undefined; canChange: boolean; onChanged: (a: ProposedAction) => void }) {
  const [open, setOpen] = useState(false);
  const meetings = useQuery({ queryKey: ['meetings', 'all', null], queryFn: () => api.meetings.list({ when: 'all' }) });
  const move = useCardChange((b: { trackId: string; activityId: string }) => api.actions.retarget(a!.id, b).then(r => { onChanged(r); return r; }));
  const activityId = a?.kind === 'file_minutes' ? a.payload.activityId : null;
  const rows = meetings.data ?? [];
  const cur = rows.find(x => x.id === activityId);
  const groups = useMemo(() => {
    const g = new Map<string, MeetingRow[]>();
    for (const x of rows) { const k = `${x.spaceName} › ${x.trackName}`; g.set(k, [...(g.get(k) ?? []), x]); }
    return [...g].sort((p, q) => p[0].localeCompare(q[0]));
  }, [rows]);
  return (
    <div className="panel" role="group" aria-label="Will file to">
      <div className="rowc btwc"><b className="sm">Will file to</b>{canChange && cur && !open && <button type="button" className="bt sm" onClick={() => setOpen(true)}>Change</button>}</div>
      {!cur ? <Skel h={28} r={8} style={{ marginTop: 8 }} /> : <>
        <div className="rvto">{cur.spaceName} › {cur.trackName}</div>
        <div className="xs mut">On {cur.title}</div>
      </>}
      {open && cur && (
        <label className="fld" style={{ marginTop: 10 }}>File the minutes on
          <select className="inp" value={cur.id} disabled={move.isPending} aria-label="File the minutes on"
            onChange={e => { const t = rows.find(x => x.id === e.target.value); if (t) move.mutate({ trackId: t.trackId, activityId: t.id }, { onSuccess: () => setOpen(false) }); }}>
            {groups.map(([k, xs]) => <optgroup key={k} label={k}>{xs.map(x => <option key={x.id} value={x.id}>{x.title}{x.startsAt ? `, ${gbDate(new Date(x.startsAt), { day: 'numeric', month: 'short', timeZone: 'Asia/Riyadh' })}` : ''}</option>)}</optgroup>)}
          </select>
        </label>
      )}
      {move.error ? <ErrorNote error={move.error} /> : null}
    </div>
  );
}

/**
 * Who is speaking: the voices the speech to text separated (ElevenLabs diarization), each with its name (or the
 * assistant's guess, or Name this voice) and its share of the talk time. Not shown when there are no separated voices.
 */
function WhoIsSpeaking({ rec }: { rec: Recording }) {
  const [open, setOpen] = useState<string | null>(null);
  const share = useMemo(() => talkShare(rec.lines), [rec.lines]);
  if (!rec.speakers.length) return null;
  return (
    <div className="panel" role="group" aria-label="Who is speaking">
      <b className="sm">Who is speaking</b>
      <div className="col" style={{ marginTop: 8 }}>
        {rec.speakers.map(s => {
          const v = speakerView(s.label, rec.speakers);
          return (
            <div className="lrow spkrow" key={s.label} data-speaker={s.label}>
              <span className="pl neu"><i className="sdot" style={{ ['--sc' as string]: speakerColor(s.label) }} />{s.label}</span>
              <span className="mut" aria-hidden="true">›</span>
              {v.state === 'unknown'
                ? <button type="button" className="bt sm" onClick={() => setOpen(s.label)}>Name this voice</button>
                : <button type="button" className={`spk ${v.state}`} onClick={() => setOpen(s.label)} aria-label={v.state === 'named' ? `${s.label}: ${v.name}` : `${s.label}: probably ${v.name}, not confirmed`}>{v.name}{v.state === 'suggested' ? '?' : ''}</button>}
              <span className="sp" />
              <span className="xs mut mono">{share.get(s.label) ?? 0}%</span>
            </div>
          );
        })}
      </div>
      {open && <NameSpeaker rec={rec} label={open} onDone={() => setOpen(null)} />}
    </div>
  );
}

/** A few lines of the transcript (from the first marked moment, if any), and the way to the whole of it. */
function Excerpt({ rec }: { rec: Recording }) {
  const from = rec.marks.length ? Math.max(0, rec.lines.findIndex(l => l.at >= rec.marks[0]!) - 1) : 0;
  const lines = rec.lines.slice(from, from + 4);
  return (
    <div className="panel" role="group" aria-label="Transcript excerpt">
      <div className="rowc btwc"><b className="sm">Transcript</b><span className="xs mut">excerpt</span></div>
      <div className="col" style={{ gap: 10, marginTop: 8 }}>
        {lines.map((l, i) => {
          const v = speakerView(l.who, rec.speakers);
          return <div key={`${l.at}-${i}`}><span className="xs mono mut">{clock(l.at)} · {v.name}{v.state === 'suggested' ? '?' : ''}</span><div className="sm">{l.t}</div></div>;
        })}
        {!lines.length && <span className="xs mut">Nothing was heard.</span>}
      </div>
      {rec.activityId && <Link className="xs ulink" style={{ display: 'inline-block', marginTop: 10 }} to={`/meetings/${rec.activityId}`}>Open the meeting, with the whole transcript</Link>}
    </div>
  );
}
